import { useRef, useEffect } from 'react';

import { useParams } from 'react-router-dom';

import { DocRow } from './doc-row';
import { DocWithFacts } from '../../../../../../../../api/types/queries';
import { Doc } from '@/api/types/backend-dto-types';
import { Icon } from '@/new/components/icon';
import { Empty, EmptyHeader, EmptyMedia, EmptyDescription, EmptyTitle } from '@/new/components/shadcn/empty';
import { Skeleton } from '@/new/components/shadcn/skeleton';

export function DocList({
  docs,
  isLoadingDocs,
  searchQuery,
  selectedDoc,
  setSelectedDoc,
  selectedDocIds,
  onDocSelectionChange,
}: {
  docs: DocWithFacts[] | undefined;
  isLoadingDocs: boolean;
  searchQuery: string;
  selectedDoc: Doc | undefined;
  setSelectedDoc: (doc: DocWithFacts) => void;
  selectedDocIds: Set<string>;
  onDocSelectionChange: (docId: string, selected: boolean) => void;
}) {
  const { matterId, subsetId } = useParams();
  if (!matterId || !subsetId) return;

  const docRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  // Scroll selected doc into view
  useEffect(() => {
    if (selectedDoc?.id) {
      const docElement = docRefs.current.get(selectedDoc.id);
      if (docElement) {
        docElement.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [selectedDoc?.id]);

  return (
    <>
      {docs && docs.length > 0 ? (
        <>
          {docs.map((doc) => {
            return (
              <DocRow
                key={doc.id}
                ref={(el) => {
                  if (el) {
                    docRefs.current.set(doc.id, el);
                  } else {
                    docRefs.current.delete(doc.id);
                  }
                }}
                doc={doc}
                subsetId={subsetId}
                searchQuery={searchQuery}
                isSelected={doc.id === selectedDoc?.id}
                onSelect={() => {
                  setSelectedDoc(doc);
                }}
                isDocSelected={selectedDocIds.has(doc.id)}
                onDocSelectionChange={onDocSelectionChange}
              />
            );
          })}
        </>
      ) : isLoadingDocs ? (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, index) => (
            <div key={index} className="rounded-md border border-gray-300 bg-white">
              <div className="flex items-center justify-between rounded-t-md border-b bg-gray-50 p-4 py-1.75 pr-1.5">
                <div className="flex items-center gap-2">
                  <Skeleton className="h-3 w-3" />
                  <Skeleton className="h-3 w-48" />
                </div>
                <Skeleton className="h-7 w-16" />
              </div>
              <div className="space-y-1 p-4 py-2 pb-2.5">
                <Skeleton className="h-3 w-32" />
                <Skeleton className="h-3 w-24" />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex items-center justify-center">
          <Empty>
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <Icon className="lucide--circle-question-mark h-5 w-5" />
              </EmptyMedia>
              <EmptyTitle>No documents found</EmptyTitle>
              <EmptyDescription>Could not find any documents in this subset.</EmptyDescription>
            </EmptyHeader>
          </Empty>
        </div>
      )}
    </>
  );
}
